/**
 * OffScreenHitIndicators — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.offScreenHitIndicators,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';
import { PixiCamera } from '../PixiCamera.js';

/**
 * Create off-screen hit indicator container
 */
export function create(hud) {
  const container = new PIXI.Container();
  hud.hudContainer.addChild(container);
  hud.elements.offScreenHitIndicators = {
    container,
    indicators: [] // Array of active indicator objects
  };
}

/**
 * Update off-screen hit indicators
 * @param {Object} gameState - Game state
 */
export function update(hud, gameState) {
  const elem = hud.elements.offScreenHitIndicators;
  if (!elem || !gameState.offScreenHitIndicators) return;

  const now = Date.now();

  // Use PixiCamera instead of gameState.camera
  if (typeof PixiCamera === 'undefined' || !PixiCamera.screenWidth) return;

  // Expiry happens in GameState.expireTransients() (update tick); hud only draws what is live.

  // Remove expired indicator graphics
  elem.indicators = elem.indicators.filter(ind => {
    if (now - ind.timestamp >= ind.duration) {
      elem.container.removeChild(ind.graphics);
      ind.graphics.destroy({ children: true }); // frees the Text texture and Graphics geometry
      return false;
    }
    return true;
  });

  // Create new indicators for hits not yet rendered
  gameState.offScreenHitIndicators.forEach(hit => {
    const exists = elem.indicators.some(ind =>
      ind.targetX === hit.targetX &&
      ind.targetY === hit.targetY &&
      ind.timestamp === hit.timestamp
    );

    if (!exists) {
      hud._createHitIndicatorGraphic(hit, elem);
    }
  });

  // Update existing indicators (fade out animation)
  elem.indicators.forEach(ind => {
    const elapsed = now - ind.timestamp;
    const progress = elapsed / ind.duration;
    const alpha = Math.max(0, 1 - progress);

    // Update position based on current camera using PixiCamera
    const screenPos = PixiCamera.worldToScreen(ind.targetX, ind.targetY);
    const screenX = screenPos.x;
    const screenY = screenPos.y;

    const centerX = hud.screenWidth / 2;
    const centerY = hud.screenHeight / 2;
    const dx = screenX - centerX;
    const dy = screenY - centerY;
    const angle = Math.atan2(dy, dx);

    const margin = 60;
    const edgeX = centerX + Math.cos(angle) * (hud.screenWidth / 2 - margin);
    const edgeY = centerY + Math.sin(angle) * (hud.screenHeight / 2 - margin);

    const clampedX = Math.max(margin, Math.min(hud.screenWidth - margin, edgeX));
    const clampedY = Math.max(margin, Math.min(hud.screenHeight - margin, edgeY));

    ind.graphics.x = clampedX;
    ind.graphics.y = clampedY;
    ind.graphics.rotation = angle;
    ind.graphics.alpha = alpha;

    // Counter-rotate the damage text so it stays readable
    // Also reposition based on direction to avoid overlapping the arrow
    if (ind.damageText) {
      ind.damageText.rotation = -angle;
      const pointingLeft = Math.abs(angle) > Math.PI / 2;
      ind.damageText.anchor.set(pointingLeft ? 1 : 0, 0.5);
      ind.damageText.x = pointingLeft ? -10 : 30;
    }
  });
}
